import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { fetchAndActivate, getString } from "firebase/remote-config";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { db, firebaseRemoteConfig } from "../../../firebase";
import { normalizeKey, isObj } from "../../../utils/common_handlers";
import { ConfirmationBox } from "./ConfirmationBox";
import { LoadingIcon } from "./LoadingIcon";
import styles from "./style/FeatureFlag.module.css";

function FeatureFlagModal({ isOpen, onRequestClose, email, uuid }) {
  const [userData, setUserData] = useState(null);
  const [featureFlag, setFeatureFlag] = useState(null);
  const [generalSettings, setGeneralSettings] = useState({});
  const [sectionSettings, setSectionSettings] = useState([]);
  const [updatedData, setUpdatedData] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [changedFields, setChangedFields] = useState({});
  const [userId, setUserId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const remoteConfig = firebaseRemoteConfig; // Use firebaseRemoteConfig from firebase.js

  // Define fetchData as an async function to fetch both remote config and user data
  const fetchData = useCallback(async () => {
    // If it's the initial load, set the loading state to true
    if (initialLoad) {
      setIsLoading(true);
    }

    try {
      // Fetch Remote Config from Firebase
      await fetchAndActivate(remoteConfig);
      const remoteFeatureFlag = await JSON.parse(
        getString(remoteConfig, "FeatureFlag")
      );

      // Set the fetched feature flag to the component state
      setFeatureFlag(remoteFeatureFlag);

      // Fetch User Data from Firestore using UUID directly
      const userDocRef = doc(db, "UserData", uuid);
      const userSnapshot = await getDoc(userDocRef);

      if (userSnapshot.exists()) {
        setUserId(uuid);
        const userData = userSnapshot.data();
        const mergedFeatureFlag = mergeConfigData(
          remoteFeatureFlag,
          userData.FeatureFlag
        );
        setUserData({ ...userData, FeatureFlag: mergedFeatureFlag });
        const userWithDefaults = fillMissingFromRemote(
          userData.FeatureFlag || {},
          remoteFeatureFlag || {}
        );
        setFeatureFlag(userWithDefaults);
      }
    } catch (error) {
      console.log(error);
    }

    // If it's the initial load, set the loading state to false and initial load state to false
    if (initialLoad) {
      setIsLoading(false);
      setInitialLoad(false);
    }
  }, [remoteConfig, uuid, initialLoad]);

  // Build a map from normalized key -> actual user key for O(1) matching
  const indexKeys = (obj) => {
    const map = new Map();
    if (!isObj(obj)) return map;
    for (const k of Object.keys(obj)) map.set(normalizeKey(k), k);
    return map;
  };

  /**
   * Fills in missing keys from the remote configuration into the user object.
   * @param {Object} user - The user object to fill.
   * @param {Object} remote - The remote configuration object.
   * @returns {Object} - The filled user object.
   */
  const fillMissingFromRemote = (user, remote) => {
    // Shallow clone user (we'll create deeper objects only when needed)
    const out = isObj(user) ? { ...user } : user ?? {};

    const applyRemoteDefaults = (currentUser, fetchedData) => {
      // Stop if either is not an object
      if (!isObj(fetchedData) || !isObj(currentUser)) return;

      // Stack holds pairs of nodes we need to process
      const stack = [{ u: currentUser, r: fetchedData }];

      while (stack.length) {
        const { u, r } = stack.pop();
        if (!isObj(r) || !isObj(u)) continue;

        const userKeyIndex = indexKeys(u);

        for (const [rKey, rVal] of Object.entries(r)) {
          const uMatchKey = userKeyIndex.get(normalizeKey(rKey));

          if (uMatchKey !== undefined) {
            // User already has this key
            const uVal = u[uMatchKey];
            // If both are objects, process that subtree later
            if (isObj(rVal) && isObj(uVal)) {
              stack.push({ u: uVal, r: rVal });
            }
          } else {
            // Missing in user → add remote default (deep copy to avoid shared refs)
            u[rKey] = isObj(rVal) ? structuredClone(rVal) : rVal;
          }
        }
      }
    };

    if (isObj(remote)) {
      // Ensure top-level is an object
      if (!isObj(out)) return structuredClone(remote);
      applyRemoteDefaults(out, remote);
    }

    return out;
  };

  const mergeConfigData = (obj1, obj2) => {
    // Start with a shallow copy of obj1
    const output = { ...obj1 };

    // Iterate through the keys of obj2
    Object.keys(obj2).forEach((key) => {
      // Check if the current key exists in both objects and its value is an object
      if (
        obj1[key] &&
        typeof obj1[key] === "object" &&
        !Array.isArray(obj1[key])
      ) {
        // If the value is an object, merge the nested objects recursively
        output[key] = mergeConfigData(obj1[key], obj2[key]);
      } else {
        // If the value is not an object, assign the value from obj2 to the output object
        output[key] = obj2[key];
      }
    });

    // Return the merged configuration object
    return output;
  };

  useEffect(() => {
    // Call fetchData if the modal is open or if a refresh is triggered
    const getData = async () => {
      await fetchData();
    };

    if (isOpen || refresh) {
      getData();
      // If a refresh is triggered, reset the refresh state to false after fetching data
      if (refresh) {
        setRefresh(false);
      }
    }

    // Run the useEffect hook whenever isOpen or refresh states change
  }, [isOpen, refresh, fetchData, remoteConfig]);

  useEffect(() => {
    if (!featureFlag) return;

    const newGeneralSettings = {};
    const newSectionSettings = [];

    Object.entries(featureFlag).forEach(([key, value]) => {
      if (typeof value !== "object" || Array.isArray(value)) {
        newGeneralSettings[key] = value;
      } else if (key === "tests") {
        const imvi = {};
        const others = {};

        // Separate the tests into tests and imvi_tests
        Object.entries(value).forEach(([testKey, testValue]) => {
          if (testKey.startsWith("imvi")) {
            imvi[testKey] = testValue;
          } else {
            others[testKey] = testValue;
          }
        });

        // Add the separated tests to the new section settings
        newSectionSettings.push({ key: "tests", value: others });

        // Add the imvi tests to the new section settings if there are any
        if (Object.keys(imvi).length > 0) {
          newSectionSettings.push({ key: "imvi_tests", value: imvi });
        }
      } else {
        // For other sections, push the key-value pair to sectionSettings
        newSectionSettings.push({ key, value });
      }
    });

    const dynamicSectionKeys = Object.entries(featureFlag)
      .filter(
        ([key, value]) => typeof value === "object" && !Array.isArray(value)
      )
      .map(([key]) => key);

    const insertAfter = (list, target, toInsert) => {
      const index = list.indexOf(target);
      if (index === -1) return list;
      return [...list.slice(0, index + 1), toInsert, ...list.slice(index + 1)];
    };

    let orderedSections = [...dynamicSectionKeys];

    // Inject imvi_tests after tests
    if (
      Object.keys(featureFlag.tests || {}).some((k) => k.startsWith("imvi"))
    ) {
      orderedSections = insertAfter(orderedSections, "tests", "imvi_tests");
    }

    // Sort the new section settings based on the order of dynamicSectionKeys
    newSectionSettings.sort((a, b) => {
      const indexA = orderedSections.indexOf(a.key);
      const indexB = orderedSections.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    setGeneralSettings(newGeneralSettings);
    setSectionSettings(newSectionSettings);
  }, [featureFlag]);

  // Define a function to reset feature flags to their default values
  const resetToDefault = async () => {
    // Create an object with the same keys as userData.FeatureFlag but with values from remoteFeatureFlag
    const resetData = Object.keys(userData.FeatureFlag).reduce((acc, key) => {
      return { ...acc, [key]: featureFlag[key] };
    }, {});

    // Set the updatedData to the original featureFlag data
    setUpdatedData(resetData);

    // Clear the changedFields object to indicate no fields are modified
    setChangedFields({});

    // Set a confirmation message to inform the user that default values have been restored
    setConfirmationMessage(
      "Default values successfully restored.\n Please save to apply changes!"
    );

    // Show the confirmation message to the user
    setShowConfirmation(true);
  };

  // Define a function to resolve the section key based on the provided section name
  const resolveSectionKey = (section) => {
    if (!section) return null;
    if (/_?tests$/i.test(section)) return "tests";
    if (section === "general") return null;
    return section;
  };

  // Define a function to update the feature flag data
  const updateFeatureFlag = (key, value, section = null) => {
    // Get the provided section, if section is general, use the key directly
    const resolvedSection = resolveSectionKey(section);
    const fullKey = resolvedSection ? `${resolvedSection}.${key}` : key;

    // Update the updatedData object with the new key-value pair
    setUpdatedData({ ...updatedData, [fullKey]: value });

    // Update the changedFields object to indicate the field has been modified
    setChangedFields({ ...changedFields, [fullKey]: true });
  };

  // Define the function to save the updated feature flags data
  const handleSave = async (updatedData) => {
    try {
      const userDocRef = doc(db, "UserData", userId);
      const userSnapshot = await getDoc(userDocRef);

      const updatedValues = Object.entries(updatedData).reduce(
        (acc, [key, value]) => {
          if (JSON.stringify(value) !== JSON.stringify(featureFlag[key])) {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );

      const featureFlagDataToSave = {
        ...featureFlag,
        ...updatedValues,
      };

      if (!userSnapshot.exists()) {
        await setDoc(userDocRef, {
          FeatureFlag: featureFlagDataToSave,
        });
      } else {
        const updatedFeatureFlagData = Object.keys(
          featureFlagDataToSave
        ).reduce((acc, key) => {
          return { ...acc, [`FeatureFlag.${key}`]: featureFlagDataToSave[key] };
        }, {});

        await updateDoc(userDocRef, updatedFeatureFlagData);
      }

      setFeatureFlag(featureFlagDataToSave);

      setConfirmationMessage(
        `New data for ${email} successfully saved.\n You can close this window now.`
      );
      setShowConfirmation(true);
      setRefresh(true);
    } catch (error) {
      setConfirmationMessage(`Error: ${error.message}`);
      setShowConfirmation(true);
    }
  };

  const formatLabel = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  const renderItem = (key, value, section = null) => {
    const id = section ? `${section}-${key}` : key;

    let fullKey = "";
    if (section === "imvi_tests") {
      fullKey = `tests.${key}`;
    } else if (section === "general" || section === null) {
      fullKey = key; // top-level setting
    } else {
      fullKey = `${section}.${key}`;
    }

    const updatedDataValue =
      updatedData && updatedData[fullKey] !== undefined
        ? updatedData[fullKey]
        : undefined;

    const userDataValue =
      userData &&
      userData.FeatureFlag &&
      userData.FeatureFlag[fullKey] !== undefined
        ? userData.FeatureFlag[fullKey]
        : undefined;

    const currentValue =
      updatedDataValue !== undefined
        ? updatedDataValue
        : userDataValue !== undefined
        ? userDataValue
        : value;

    if (typeof value === "number") {
      return (
        <div key={key} className="mb-2">
          <div className="flex justify-between items-center">
            <label htmlFor={id} className="text-sm text-gray-700">
              {formatLabel(key) + ":"}
            </label>
            <input
              id={id}
              type="number"
              value={currentValue}
              onChange={(e) =>
                updateFeatureFlag(key, parseInt(e.target.value), section)
              }
              className="border border-gray-300 rounded px-2 py-1 w-16 text-sm"
              min="0"
            />
          </div>
        </div>
      );
    }

    if (typeof value === "boolean") {
      return (
        <div key={key} className="mb-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={id}
              checked={currentValue}
              onChange={(e) =>
                updateFeatureFlag(key, e.target.checked, section)
              }
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              style={{ marginRight: "8px" }}
            />
            <label
              htmlFor={id}
              className="text-sm text-gray-700 cursor-pointer"
            >
              {formatLabel(key)}
            </label>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderSection = (sectionName, sectionData) => {
    return (
      <div key={sectionName}>
        <h3 className="font-semibold text-gray-800 mb-4 text-base">
          {formatLabel(sectionName)}
        </h3>
        <div className="space-y-1">
          {Object.entries(sectionData)
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // Sort entries alphabetically by key
            .map(([key, value]) => renderItem(key, value, sectionName))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Feature Flags"
      appElement={document.getElementById("root")}
    >
      {" "}
      <div className={styles.header_container}>
        <h2 className={styles.header}>Feature Flags</h2>
      </div>
      <div>
        {isLoading ? (
          <div className={styles.modal}>
            <LoadingIcon />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className={styles.modal}>
              {featureFlag && (
                <div className={styles.section_grid}>
                  {/* General Settings box */}
                  <div className={styles.section_box}>
                    {renderSection("general", generalSettings)}
                  </div>
                  {sectionSettings
                    .sort(({ key: keyA }, { key: keyB }) =>
                      keyA.localeCompare(keyB)
                    ) // Sort sections alphabetically by key
                    .map(({ key, value }) => (
                      <div key={key} className={styles.section_box}>
                        {renderSection(key, value)}
                      </div>
                    ))}
                </div>
              )}
              {showConfirmation && (
                <ConfirmationBox
                  message={confirmationMessage}
                  onClose={() => {
                    setShowConfirmation(false);
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button onClick={resetToDefault} className={styles.button_reset}>
                Set Default values
              </button>
              <button
                onClick={() => handleSave(updatedData)}
                className={styles.button_save}
              >
                Save Changes
              </button>
              <button
                onClick={() => onRequestClose()}
                className={styles.button_close}
              >
                Close Feature Flag Window
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default FeatureFlagModal;
